import { Application, Container } from '@tencent/alloy-renderer';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import type { BackgroundManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/background/background-manager';
import type { MasterManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/master-layout/master-manager';
import type { LayoutManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/master-layout/layout-manager';
import { IStableLayerProps, IRenderProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { UIState } from '@src/slide/infrastructure/module/independent/ui-state';

export interface IStableLayerComponent {
    backgroundComponent: Container | undefined;
    masterComponent: Container | undefined;
    layoutComponent: Container | undefined
}

export class StableLayerService {
    public container: Container;
    public isDirty: boolean;
    private stableLayerWrapper: HTMLDivElement;
    private backgroundManager: BackgroundManager;
    private masterManager: MasterManager;
    private layoutManager: LayoutManager;
    private application: Application;
    private scale: number;
    private slideRefs: ISlideModel[];
    private removeStableLayer: (layer) => void;
    private stableLayerComponent: IStableLayerComponent | null;

    constructor(props: IStableLayerProps) {
        this.stableLayerWrapper = props.stableLayerWrapper;
        this.backgroundManager = props.backgroundManager;
        this.masterManager = props.masterManager;
        this.layoutManager = props.layoutManager;
        this.scale = props.scale;
        this.removeStableLayer = props.removeStableLayer;
        this.container = new Container();
        this.isDirty = false;
        this.slideRefs = [];
        this.buildApplication();
    }

    public updateInfo(scale: number) {
        if (scale !== this.scale) {
            this.scale = scale;
            this.isDirty = true;
        }
    }

    public show(): void {
        if ((this.application.view.native as HTMLCanvasElement).style) {
            (this.application.view.native as HTMLCanvasElement).style.display = 'block';
        }
    }

    public hide(): void {
        if ((this.application.view.native as HTMLCanvasElement).style) {
            (this.application.view.native as HTMLCanvasElement).style.display = 'none';
        }
    }

    /**
     * 渲染稳定层
     * @param props
     */
    public render(props: IRenderProps): void {
        this.isDirty = false;
        this.stableLayerComponent = this.getStableLayerComponent(props);
        // 先移除 container 中子元素
        this.container.children.forEach(item => this.container.removeChild(item));
        this.container.x = 0;
        this.container.y = 0;
        if (this.stableLayerComponent.backgroundComponent) {
            this.container.addChild(this.stableLayerComponent.backgroundComponent);
        }
        if (this.stableLayerComponent.masterComponent) {
            this.container.addChild(this.stableLayerComponent.masterComponent);
        }
        if (this.stableLayerComponent.layoutComponent) {
            this.container.addChild(this.stableLayerComponent.layoutComponent);
        }
        this.application.stage.addChild(this.container);
        this.application.stage.transform.scaleX = this.scale;
        this.application.stage.transform.scaleY = this.scale;

        if (__IS_SSR__) {
            // ssr中直接使用和slide一样的画布信息即可
            this.application.render();
            return;
        }

        // 获取稳定层元素 bounds，使用 bounds 设置画布宽高
        const { width, height, x, y } = this.container.filterWorldBounds;
        this.container.x = -x / this.scale;
        this.container.y = -y / this.scale;

        if (!UIState.isExportingToImage) {
            this.application.width = Math.round(width);
            this.application.height = Math.round(height);
            this.application.render();
        }
    }

    public dispose(): void {
        this.application.view.width = 0;
        this.application.view.height = 0;
        this.stableLayerWrapper.removeChild((this.application.view.native as HTMLCanvasElement));
        // 母版和板式渲染树其他 layer 可能在使用，销毁渲染树时应当将母版和板式渲染树从 container 中移除
        if (this.container.children.length > 0) {
            if (this.stableLayerComponent?.masterComponent) {
                this.container.removeChild(this.stableLayerComponent.masterComponent);
            }
            if (this.stableLayerComponent?.layoutComponent) {
                this.container.removeChild(this.stableLayerComponent.layoutComponent);
            }
        }
        this.stableLayerComponent = null;
        this.application.dispose();
    }

    /**
     * 将使用当前 stableLayer 的 slideModel 加入 slideRefs 中
     * @param slideModel
     */
    public addSlideModel(slideModel: ISlideModel) {
        const index = this.slideRefs.indexOf(slideModel);
        if (index !== -1) {
            return;
        }
        this.slideRefs.push(slideModel);
    }

    /**
     * 从 slideRefs 中移除不再使用当前 stableLayer 的 slideModel，如果 slideRefs 为空，从 manager 中删除 stableLayer 缓存
     * @param slideModel
     */
    public removeSlideModel(slideModel: ISlideModel) {
        const index = this.slideRefs.indexOf(slideModel);
        if (index === -1) {
            return;
        }
        this.slideRefs.splice(index, 1);
        if (this.slideRefs.length === 0) {
            this.removeStableLayer(this);
        }
    }

    public getApplication = (): Application => this.application;

    private getStableLayerComponent(props: IRenderProps): IStableLayerComponent {
        const { slideModel, masterModel, layoutModel } = props;
        const layoutComponent = this.layoutManager.getRenderComponent?.(layoutModel);
        const masterComponent = this.masterManager.getRenderComponent?.(masterModel);
        const backgroundComponent = this.backgroundManager.getRenderComponent?.(slideModel);
        const showMasterSp = slideModel?.getRelationLayoutModel()?.showMasterSp;
        const showMasterAndLayoutSp = slideModel?.showMasterSp;
        return {
            layoutComponent: showMasterAndLayoutSp ? layoutComponent : undefined,
            masterComponent: (showMasterSp && showMasterAndLayoutSp) ? masterComponent : undefined,
            backgroundComponent,
        };
    }

    private buildApplication() {
        const canvas = document.createElement('canvas');
        canvas.style.position = 'absolute';
        canvas.style.left = '0';
        canvas.style.top = '0';
        canvas.style.display = 'none';
        canvas.id = 'stable-layer';
        const target = new BrowserTarget();
        this.application = new Application(target, {
            native: canvas,
            interactive: false,
            autoRender: false,
        });
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        this.stableLayerWrapper.append(canvas);
    }
}
